import { Box, Button, Tooltip, TextField } from '@mui/material';
import { useState, FC, useEffect, useCallback, useMemo } from 'react';
import styles from 'pages/program-case/ProgramCase.module.css';
import { useLocation, useNavigate } from 'react-router-dom';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useAppDispatch, useAppSelector } from 'shared/utils/hooks/useRedux';
import { fetchProgramCase, fetchProgramDownload } from 'entities/program-case/actions/fetch-program-case';
import { CurrentPageProgram, Download, ErrorDownload, FileData, FileName, LoadingCaseProgram, PrintNameCaseProgram, СaseProgram } from 'entities/program-case/selector';
import List from 'widgets/list/list';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocalHotelIcon from '@mui/icons-material/LocalHotel';
import PsychologyIcon from '@mui/icons-material/Psychology';
import { FaTooth } from "react-icons/fa";
import { GiMilitaryAmbulance } from "react-icons/gi";
import { TbCircleLetterBFilled } from "react-icons/tb";
import { TbCircleLetterTFilled } from "react-icons/tb";
import { resetFileData, setCurrentPage } from 'entities/program-case';
import ModalWidget from 'widgets/modal/Modal';
import TransferList from 'widgets/field-settings/FieldSettings';
import { allFields, defaultFields, fieldMappings } from 'features/program-case/utils/constant';
import SettingsIcon from '@mui/icons-material/Settings';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CustomButton from 'shared/custom-button/CustomButton';
import { IAlertInfo, IHospitals } from 'entities/program-case/types/program-case-interface';
import { AlertMessage } from 'shared/alert-message/alert-message';
import SearchIcon from '@mui/icons-material/Search';
import appStyles from 'app/providers/theme/App.module.css';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import Loader from 'shared/loader/Loader';
import CurrencyRubleIcon from '@mui/icons-material/CurrencyRuble';

interface IProgramCase {
    isContract?: boolean;
    contrsctProgramID?: string;
}

export const ProgramCase: FC<IProgramCase> = ({ isContract, contrsctProgramID }) => {
    const dispatch = useAppDispatch();
    const useQuery = () => new URLSearchParams(useLocation().search);
    const query = useQuery();
    const queryProgramID = query.get('id');
    const caseProgramData = useAppSelector(СaseProgram);
    const printName = useAppSelector(PrintNameCaseProgram);
    const navigate = useNavigate();
    const location = useLocation();
    const currentPage = useAppSelector(CurrentPageProgram);
    const isLoading = useAppSelector(LoadingCaseProgram);
    const [fields, setFields] = useState<string[]>(defaultFields);
    const [tempFields, setTempFields] = useState<string[]>(fields);
    const [open, setOpen] = useState(false);
    const [alertInfo, setAlertInfo] = useState<IAlertInfo>({ message: '', severity: '' });
    const [downloadTriggered, setDownloadTriggered] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [openSearch, setOpenSearch] = useState(false);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const handleOpenSearch = () => setOpenSearch((prevState) => !prevState);

    useEffect(() => {
        dispatch(resetFileData());
    }, [dispatch]);

    const handleClickBack = () => {
        if (location.state?.fromInsuranceContractPage) {
            navigate(`/insurance-contracts/case/?id=${location.state.id}`, {
                state: {
                    textRout: 'insurance-contracts',
                    name: 'Договоры страхования',
                    activeTab: location.state.activeTab
                }
            });
        } else {
            navigate('/insurance-programs', {
                state: {
                    textRout: 'insurance-programs',
                    name: 'Программы страхования'
                }
            });
        }
    };

    const limit = 10;


    useEffect(() => {
        const sortArray: string[] | [] = [];
        if (isContract && contrsctProgramID) {
            dispatch(fetchProgramCase({ programID: contrsctProgramID, sortArray }));
        } else if (!isContract && queryProgramID) {
            dispatch(fetchProgramCase({ programID: queryProgramID, sortArray }));
        }

    }, [dispatch, queryProgramID, contrsctProgramID]);

    const handleChildAction = useCallback((page: number) => {
        dispatch(setCurrentPage({ page }));
    }, [dispatch]);

    const icons = [
        { name: 'Поликлиника', icon: <LocalHospitalIcon /> },
        { name: 'Стоматология', icon: <FaTooth /> },
        { name: 'Стационар', icon: <LocalHotelIcon /> },
        { name: 'Скорая медицинская помощь', icon: <GiMilitaryAmbulance /> },
        { name: 'Психологическая помощь', icon: <PsychologyIcon /> },
        { name: 'Вызов врача на дом', icon: <TbCircleLetterBFilled /> },
        { name: 'Телемедицина', icon: <TbCircleLetterTFilled /> },
    ];

    const getIconByName = (name: string) => {
        const icon = icons.find(icon => icon.name === name);
        return icon ? icon.icon : null;
    };

    const renderItem = (item: any) => (
        <>
            {fields.map((field) => {
                const key = fieldMappings[field];
                if (key === 'Risks') {
                    return (
                        <Box key={field}>
                            {item[key].map((risk: any) => (
                                <Tooltip key={risk.RiskCode} title={risk.RiskPrintName} placement="top">
                                    <Box display="inline-block" mr={1}>
                                        {getIconByName(risk.RiskPrintName)}
                                    </Box>
                                </Tooltip>
                            ))}
                        </Box>
                    );
                } else if (key === 'HasChildDepartment') {
                    return (
                        <Box key={field}>
                            <p>{item[key] ? 'Есть' : 'Нет'}</p>
                        </Box>
                    );
                } else if (key === 'HasFranchise') {
                    return (
                        <Box key={field}>
                            <p>{item[key] ? 'Есть' : 'Нет'}</p>
                        </Box>
                    );
                } else if (key === 'FranchiseValue') {
                    return (
                        <Box key={field}>
                            {item.FranchiseValue ? (<p>{item.FranchiseValue} %</p>) : <p>-</p>}
                        </Box>
                    );
                } else {
                    return (
                        <Box key={field}>
                            <p>{item[key]}</p>
                        </Box>
                    );
                }
            })}
        </>
    );

    const handleOpen = () => {
        setTempFields(fields);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleReset = () => {
        setTempFields(defaultFields);
    };

    const handleAccept = () => {
        setFields(tempFields);
        setOpen(false);
    };

    const handleTempFieldsChange = (newFields: string[]) => {
        setTempFields(newFields);
    };

    const fileData = useAppSelector(FileData);
    const fileName = useAppSelector(FileName);
    const isDownload = useAppSelector(Download);
    const errorDownload = useAppSelector(ErrorDownload);

    const handleFileDownload = async (dataType: string, filters?: any) => {
        setAlertInfo({ message: '', severity: '' });

        try {
            setIsDownloading(true);
            const programID = contrsctProgramID || queryProgramID;
            if (!programID) return;

            await dispatch(fetchProgramDownload({
                ProgramID: programID,
                DataType: dataType,
                Filters: filters
            }));

            setDownloadTriggered(true);
        } catch (error) {
            console.error('Error downloading the file:', error);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleDownload = async () => {
        await handleFileDownload('ПрограммаСтрахования(ОбъемУслуг)');
    };

    const excelDownload = async () => {
        const ids = filtered.map(object => object.HospitalID);
        const filters = [{
            "Field": "HospitalID",
            "ComparisonType": "IN",
            "Value": ids
        }];

        await handleFileDownload('ПрограммаСтрахования(ДанныеЛК)', filters);
    };

    useEffect(() => {
        if (downloadTriggered && fileData && errorDownload.length === 0) {
            const link = document.createElement('a');
            link.href = `data:application/octet-stream;base64,${fileData}`;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            dispatch(resetFileData());
            setDownloadTriggered(false);
        } else if (errorDownload.length > 0) {
            setAlertInfo({ message: errorDownload[0].Msg, severity: 'error' });
            const timeoutId = setTimeout(() => {
                setAlertInfo({ message: '', severity: '' });
                dispatch(resetFileData());
            }, 3000);
            return () => clearTimeout(timeoutId);
        }
    }, [downloadTriggered, fileData, errorDownload, fileName, dispatch]);

    const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        dispatch(setCurrentPage({ page: 1 }));
    }, [dispatch]);

    const filtered = (() => {
        let filteredData = [...caseProgramData];
        if (searchQuery) {
            const queryLower = searchQuery.toLowerCase();
            const isChildDepartmentSearch =
                queryLower.includes("детское") ||
                queryLower.includes("дети") ||
                queryLower.includes("дет");

            const isFranchiseSearch =
                queryLower.includes("франшиза");
            filteredData = filteredData.filter((item: IHospitals) => {
                const risksMatch = item.Risks.some(risk =>
                    risk.RiskPrintName.toLowerCase().includes(queryLower)
                );

                const baseMatch =
                    item.HospitalFullName.toLowerCase().includes(queryLower) ||
                    item.AddressName.toLowerCase().includes(queryLower) ||
                    item.MetroName.toLowerCase().includes(queryLower) ||
                    item.ServiceFormName.toLowerCase().includes(queryLower) ||
                    risksMatch;

                const childDepartmentMatch = isChildDepartmentSearch && item.HasChildDepartment;
                const franchiseMatch = isFranchiseSearch && item.HasFranchise;

                return baseMatch || childDepartmentMatch || franchiseMatch;
            });
        }
        return filteredData;
    })();

    const paginatedHospitals = () => {
        const startIndex = (currentPage - 1) * limit;
        return filtered.slice(startIndex, startIndex + limit);
    };

    return (
        <>
            {isDownload && (
                <Loader
                    title='Файл скачивается... Пожалуйста, подождите'
                />
            )}
            {isLoading && (
                <Loader
                    title='Подготовка списка. Это займет несколько секунд.'
                />
            )}
            {!isLoading && !isDownload && (
                <>
                    {alertInfo.message && (
                        <Box className={styles.alertBox}>
                            <AlertMessage alertInfo={alertInfo} />
                        </Box>
                    )}
                    {!isContract && (
                        <Box className={appStyles.titleBox}>
                            <h1>Программа страхования <span>{printName ? printName : ''}</span></h1>
                            <Box className={appStyles.titleBtn}>
                                <Button onClick={handleClickBack} className={appStyles.btnBack}><KeyboardBackspaceIcon /> Назад</Button>
                            </Box>
                        </Box>
                    )}
                    {caseProgramData.length > 0 ? (
                        <>
                            <Box className={styles.btnSettings}>
                                <Box className={styles.downloadBox}>
                                    <Tooltip title="Выгрузить в Excel" placement="top">
                                        <Box>
                                            <CustomButton
                                                icon={FileDownloadIcon}
                                                handleClick={excelDownload}
                                                textColor='white'
                                                backgroundColor="#0054E6"
                                                border='none'
                                                borderRadius='4px'
                                                padding="8px 30px"
                                                disabled={false}
                                            />
                                        </Box>
                                    </Tooltip>
                                    <Tooltip title="Скачать файл объема услуг" placement="top">
                                        <Box>
                                            <CustomButton
                                                icon={InsertDriveFileIcon}
                                                handleClick={handleDownload}
                                                textColor='#0054E6'
                                                backgroundColor="none"
                                                border='none'
                                                borderRadius='4px'
                                                padding="8px 30px"
                                                disabled={isDownloading}
                                            />
                                        </Box>
                                    </Tooltip>
                                </Box>
                                <Box className={styles.setting}>
                                    <Tooltip title="Настройка таблицы" placement="top">
                                        <span
                                            className={styles.iconContract}
                                            onClick={() => handleOpen()}
                                        >
                                            <SettingsIcon />
                                        </span>
                                    </Tooltip>
                                    <Box className={appStyles.iconSort}>
                                        <span onClick={handleOpenSearch} className={`${openSearch ? appStyles.iconSelected : ''}`}><SearchIcon /></span>
                                    </Box>
                                </Box>
                            </Box>
                            <ModalWidget
                                open={open}
                                handleClose={handleClose}
                                title="Настройка полей таблицы"
                                closeIconPosition="right"
                                showAcceptButton
                                showCancelButton
                                onAccept={handleAccept}
                                onCancel={handleReset}
                                widthProps={600}
                            >
                                <TransferList
                                    key={JSON.stringify(tempFields)}
                                    defaultFields={tempFields}
                                    allFields={allFields}
                                    onFieldsChange={handleTempFieldsChange}
                                />
                            </ModalWidget>
                            {openSearch && (
                                <TextField
                                    label="Поиск"
                                    variant="outlined"
                                    value={searchQuery}
                                    onChange={handleSearchChange}
                                    fullWidth
                                    margin="normal"
                                />
                            )}
                            {filtered.length > 0 ? (
                                <Box sx={{
                                    width: '100%',
                                    overflow: 'hidden',
                                    position: 'relative'
                                }}>
                                    <List
                                        title={fields}
                                        data={paginatedHospitals()}
                                        renderItem={renderItem}
                                        renderPagination={filtered.length > 10}
                                        pageCount={Math.ceil(filtered.length / 10)}
                                        onAction={handleChildAction}
                                        currentPage={currentPage}
                                        link={false}
                                    />
                                </Box>
                            ) : (
                                <Box className={appStyles.infoBox}>
                                    <p>Программы страхования не найдены</p>
                                </Box>
                            )}

                        </>
                    ) : (
                        <>
                            <Box className={appStyles.titleBox}>
                                <Box className={appStyles.titleBtn}>
                                    <Button onClick={handleClickBack} className={appStyles.btnBack}><KeyboardBackspaceIcon /> Назад</Button>
                                </Box>
                            </Box>
                            <Box className={appStyles.infoBox}>
                                <p>Программы страхования не найдены</p>
                            </Box>
                        </>

                    )}
                </>
            )}
        </>
    );
};

export default ProgramCase;
